import type { ResourceUser } from '@src/features/absence';
import Avatar from '@src/shared/ui/Avatar';
import type {
  AutocompleteOption,
  AutocompleteProps,
  AutocompleteRenderOptionState,
  InputProps,
} from '@src/shared/ui/inputs';
import { AutocompleteInput, InputAdornment } from '@src/shared/ui/inputs';
import { ListItem, ListItemAvatar, ListItemText } from '@src/shared/ui/List';
import type { HTMLAttributes, Key } from 'react';
import { forwardRef } from 'react';

interface UserAutocompleteOption extends AutocompleteOption {
  avatar: string | undefined;
}

type UserAutocompleteInputProps = Omit<
  AutocompleteProps<UserAutocompleteOption, false, false, false>,
  'renderInput' | 'value' | 'options' | 'onChange'
> & {
  value?: UserAutocompleteOption['value'] | null;
  users: ResourceUser[];
  onChange: (value: UserAutocompleteOption['value'] | null) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  inputSlotProps: InputProps;
  label?: React.ReactNode;
  placeholder: string;
};

const StyleSheet = {
  field: {
    '& .MuiInputBase-adornedStart': {
      paddingLeft: '3px',
    },
  },
  avatarAdornment: {
    mr: 0,
    ml: 0.75,
  },
  avatar: {
    width: '30px',
    height: '30px',
  },
};

const UserAutocompleteInput = forwardRef<
  HTMLInputElement,
  UserAutocompleteInputProps
>((props, ref) => {
  const { users, value, ...restProps } = props;
  const renderOption = (
    props: HTMLAttributes<HTMLLIElement> & { key: Key },
    option: ResourceUserOption,
    _state: AutocompleteRenderOptionState,
  ) => {
    const { key, ...rest } = props;
    return (
      <ListItem key={key} {...rest}>
        <ListItemAvatar>
          <Avatar src={option.avatar} />
        </ListItemAvatar>
        <ListItemText>{option.label}</ListItemText>
      </ListItem>
    );
  };
  const valueAvatar = () => {
    const portrait = users.find((u) => u.id === value)?.portrait;
    if (portrait) return portrait;
  };
  return (
    <AutocompleteInput
      value={value}
      ref={ref}
      {...restProps}
      options={usersToOptions(users)}
      renderOption={renderOption}
      sx={StyleSheet.field}
      inputSlotProps={{
        startAdornment: value && (
          <InputAdornment position="start" sx={StyleSheet.avatarAdornment}>
            <Avatar src={valueAvatar()} sx={StyleSheet.avatar} />
          </InputAdornment>
        ),
      }}
    />
  );
});

type ResourceUserOption = {
  label: string;
  value: string | number;
  avatar: string | undefined;
};

function usersToOptions(users: ResourceUser[]) {
  return users.map((user) => ({
    label: user.name,
    value: user.id,
    avatar: user.portrait || undefined,
  }));
}
export default UserAutocompleteInput;
